import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface BreadcrumbProps {
  // Flash messages
  flashMessages?: React.ReactNode;
  // Dados da residência/condomínio
  commercialServiceName?: string;
  residenceName?: string;
  residenceId?: string;
  // Navegação
  urlForHomeMenu: string;
  breadCrumbSection?: string;
  // Período (opcional - quando presente, esconde o Home link)
  beginningOfPeriod?: string;
  // Tipo de página para cores contextuais
  pageType?: 'water' | 'gas' | 'energy';
}

// Posições de sprite para ícones do breadcrumb por tipo de página
const iconPositions = {
  water: {
    settings: '-63px -248px',
    reports: '-107px -246px',
    goals: '-93px -247px',
    calc: '-78px -245px',
  },
  gas: {
    settings: '-63px -263px',
    reports: '-107px -261px',
    goals: '-93px -262px',
    calc: '-78px -261px',
  },
  energy: {
    settings: '-63px -278px',
    reports: '-107px -276px',
    goals: '-93px -277px',
    calc: '-78px -275px',
  },
};

// Cores do título por tipo de página
const titleColors = {
  water: '#455a64',
  gas: '#f77e49',
  energy: '#7fd119',
};

/**
 * Breadcrumb - Navegação de migalhas de pão
 * 
 * Exibe a localização atual do usuário na hierarquia do app.
 * Os ícones mudam de cor baseado no tipo de página (water/gas/energy)
 * via prop pageType (anteriormente controlado pelo CSS).
 */
export function Breadcrumb({
  flashMessages,
  commercialServiceName,
  residenceName: _residenceName,
  residenceId: _residenceId,
  urlForHomeMenu,
  breadCrumbSection,
  beginningOfPeriod,
  pageType = 'water',
}: BreadcrumbProps) {
  const { t } = useTranslation();

  // Classes do ícone base (sprite position vem inline agora)
  const icoBaseClasses = `
    ico
    inline-block
    w-[16px] h-[17px]
    mx-[6px] -mt-[1px]
    bg-[url('/images/icons-set.png')] bg-no-repeat
  `;

  // Posições de sprite para o tipo de página atual
  const positions = iconPositions[pageType];
  const titleColor = titleColors[pageType];

  return (
    <>
      {/* Page titles row */}
      <div className="
        flex flex-wrap
        mt-0 mx-0 mb-[30px]
        pt-4 px-4 pb-0
        md:pt-[30px] md:px-[46px]
        relative z-10
      ">
        <div className="w-full md:w-7/12 self-center">
          {/* Flash messages */}
          <div id="flash_messages_html_id">{flashMessages}</div>
          
          {/* Nome do condomínio/serviço */}
          {commercialServiceName && (
            <div className="mb-[3px]">{commercialServiceName}</div>
          )}
          
          {/* Breadcrumb navigation */}
          <ol className="
            breadcrumb
            flex flex-wrap list-none
            p-0 m-0 mb-0
            bg-transparent
            text-[14px]
          ">
            {/* Home link - escondido quando tem período */}
            <li
              className={`
                breadcrumb-item
                text-[12px]
                flex items-center
                after:content-['•'] after:inline-block after:mx-[5px] after:ml-[8px] after:text-[#aeaeae]
                ${beginningOfPeriod ? 'hidden' : ''}
              `}
            >
              <i className={`
                ${icoBaseClasses}
                home
                !w-[20px] !h-[20px]
                bg-[-43px_-246px]
                mr-[6px] ml-0
              `}></i>
              <Link to={urlForHomeMenu} className="text-[#455a64] hover:text-[#1976d2]">
                Home
              </Link>
            </li>
            
            {/* Seção: Ajustes */}
            {breadCrumbSection === 'adjustments' && (
              <li className="breadcrumb-item text-[12px]" style={{ color: titleColor }}>
                <i 
                  className={`${icoBaseClasses}`}
                  style={{ backgroundPosition: positions.settings }}
                ></i>
                {t('views.devise.registrations.adjustments', 'Ajustes')}
              </li>
            )}
            
            {/* Seção: Relatórios */}
            {breadCrumbSection === 'report' && (
              <li className="breadcrumb-item text-[12px]" style={{ color: titleColor }}>
                <i 
                  className={`${icoBaseClasses}`}
                  style={{ backgroundPosition: positions.reports }}
                ></i>
                {t('views.consumption.report.title', 'Relatórios')}
              </li>
            )}
            
            {/* Seção: Sua meta */}
            {breadCrumbSection === 'goal' && (
              <li className="breadcrumb-item text-[12px]" style={{ color: titleColor }}>
                <i 
                  className={`${icoBaseClasses}`}
                  style={{ backgroundPosition: positions.goals }}
                ></i>
                {t('views.consumption.goal.title', 'Sua meta')}
              </li>
            )}
            
            {/* Seção genérica */}
            {breadCrumbSection && breadCrumbSection !== 'adjustments' && breadCrumbSection !== 'report' && breadCrumbSection !== 'goal' && (
              <li className="breadcrumb-item text-[12px]">{breadCrumbSection}</li>
            )}
          </ol>
          
          {/* Início do período */}
          {beginningOfPeriod && (
            <div className="phase flex flex-wrap items-baseline gap-x-[5px]">
              <span className="phase-title text-[14px]">
                <i className="fa fa-flag-o mr-[5px]"></i>
                {t('views.layouts.breadcrumb.period_start', 'Início do Período')}:
              </span>
              <span className="phase-used text-[18px] font-light text-[#455a64]">
                {beginningOfPeriod}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Divider line - visível apenas em telas maiores */}
      <div className="col divline hidden max-[812px]:block">
        <hr />
      </div>
    </>
  );
}
